import { OutgoingRequest } from '@wui/jssip/lib/SIPMessage'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useStoreContacts } from '@/entities/contact'

import { NotifyEventData } from '@/shared/components/notifications'
import { useCallsNotifications } from '@/shared/composables/calls-notifications'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useUserFeatures } from '@/shared/composables/user-features'
import { notifyBodyParser, useWebRTC } from '@/shared/services/jssip'
import { FeaturesNames } from '@/shared/types/settings'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'

export interface PickupGroup {
  guid: string
  type: string
  name: string
  aclEnabled: boolean
  subscribers: Array<{number: string}>
}
export enum CallStatusState {
  UNKNOWN = 'unknown',
  TERMINATED = 'terminated',
  EARLY = 'early',
  CONFIRMED = 'confirmed',
  HOLD = 'hold',
}
export interface SubscriberStatus {
  internalNumber: string
  sessionId: string
  registered?: boolean
  remote?: CallStatusState
  local?: CallStatusState
  callId?: string | null
  callFromNumber?: string
}

export interface SubscriberStatusWithTime extends SubscriberStatus {
  timestamp: number
}

export const useStorePickupCall = defineStore('pickupCalls', () => {
  const pickupGroups = ref<Array<PickupGroup>>([])
  const _pickupStatuses = ref(new Map<string, SubscriberStatus>())
  const activePickupStatuses = ref(new Map<string, SubscriberStatusWithTime>())

  const fetchAndSubscribePickupCalls = (): Promise<PickupGroup[]> => {
    return new Promise((resolve) => {
      const fetch = useFetchApi()
      fetch.get<PickupGroup[]>(getAppURL({ pathName: '/api/call-interception/' })).then((response) => {
        pickupGroups.value = response.data
        const uniqueNumbers = new Set(pickupGroups.value.flatMap((group) => {
          return group.subscribers.map((subscriber) => {
            return subscriber.number
          })
        }))
        _subscribe(uniqueNumbers)
        resolve(response.data)
      })
    })
  }

  const _subscribe = (numbers: Set<string>) => {
    const { enabledFeatures } = useUserFeatures()
    if (enabledFeatures.value.has(FeaturesNames.SUBSCRIBER_PICKUP)) { // Если группа доступа отключена, не подписываемся на пользователей
      const { subscribe } = useWebRTC()
      numbers.forEach((number) => {
        const subscriber = subscribe(number)
        subscriber.on('notify', (isFinal: boolean, request: OutgoingRequest, body: string) => {
          _onNotifyHandler(request, body)
        })
      })
    }
  }

  const _onNotifyHandler = (notify: OutgoingRequest, body: string) => {
    const { currentUser } = useCurrentUser()
    const { findByInternalNumber } = useStoreContacts()
    const notifyBody = notifyBodyParser(body)
    const sessionId = notify.call_id as string
    const status: SubscriberStatus = { ...notifyBody, sessionId }
    const myNumber = currentUser.value?.internalNumber
    const notIAmCaller = myNumber !== notifyBody.callFromNumber

    const ringingStatus = notifyBody.remote && notifyBody.remote === CallStatusState.EARLY
    const terminatedStatus = notifyBody.remote && notifyBody.remote === CallStatusState.TERMINATED
    const confirmedStatus = notifyBody.remote && notifyBody.remote === CallStatusState.CONFIRMED
    const unknownStatus = notifyBody.remote && notifyBody.remote === CallStatusState.UNKNOWN

    const callId = status.callId
    if (notIAmCaller && ringingStatus && callId) {
      const { t } = useLocalization()
      let name = t('common.externalContact')
      let fromNumber = ''
      if (status.callFromNumber) {
        const contact = findByInternalNumber(status.callFromNumber)
        if (contact) {
          fromNumber = contact.internalNumber
          name = contact.name
        } else if (status.callFromNumber) {
          name = status.callFromNumber
          fromNumber = status.callFromNumber
        }
      }
      const getContact = findByInternalNumber(status.internalNumber)
      const targetNumber = getContact || { internalNumber: status.internalNumber, name: status.internalNumber }

      const isInitiator = notifyBody.direction === 'initiator'
      const callerNumber = isInitiator ? status.internalNumber : (fromNumber ?? status.callFromNumber ?? '')
      const callerName = isInitiator ? targetNumber.name : name
      const calleeNumber = isInitiator ? (fromNumber ?? status.callFromNumber ?? '') : targetNumber.internalNumber
      const calleeName = isInitiator ? name : targetNumber.name

      const notifyEvent: NotifyEventData = {
        from: callerNumber,
        fromName: callerName,
        target: calleeNumber,
        targetName: calleeName,
        callId,
      }
      useCallsNotifications().add(notifyEvent)
    } else if (callId && (terminatedStatus || confirmedStatus || unknownStatus)) {
      useCallsNotifications().remove(callId)
    }

    _pickupStatuses.value.set(status.internalNumber, status)
    if (notIAmCaller && status.remote === CallStatusState.EARLY) {
      activePickupStatuses.value.set(status.internalNumber, {
        ...status,
        timestamp: Date.now(),
      })
    } else if (activePickupStatuses.value.has(status.internalNumber)) {
      activePickupStatuses.value.delete(status.internalNumber)
      useCallsNotifications().removeBySubscriberNumber(status.internalNumber)
    }
  }

  return {
    pickupGroups,
    activePickupStatuses,
    fetchAndSubscribePickupCalls,
  }
})
